// 成绩查询 API 服务
const API_BASE_URL = 'http://mingsheng.mt-sung.com/test'

// 成绩类型映射
const SCORE_TYPE_MAP = {
  '音教成绩': 'music_education_score',
  '器表成绩': 'performance_instrumental_score',
  '声表成绩': 'performance_vocal_score'
}

// 成绩类型反向映射
const SCORE_TYPE_REVERSE_MAP = {
  'music_education_score': '音教成绩',
  'performance_instrumental_score': '器表成绩',
  'performance_vocal_score': '声表成绩'
}

/**
 * 将学生姓名的第二个字符替换为 *
 * @param {string} name - 学生姓名
 * @returns {string} 处理后的姓名
 */
function maskStudentName(name) {
  if (!name || typeof name !== 'string' || name.length < 2) {
    return name || '未知'
  }
  const nameArray = name.split('')
  nameArray[1] = '*'
  return nameArray.join('')
}

/**
 * 查询学生成绩列表
 * @param {Object} params - 查询参数
 * @param {string} params.scoreType - 成绩类型（音教成绩、器表成绩、声表成绩）
 * @param {number} params.minScore - 最小分数
 * @param {number} params.maxScore - 最大分数
 * @param {number} params.page - 页码
 * @param {number} params.pageSize - 每页数量
 * @returns {Promise} 返回查询结果
 */
export function queryScores(params) {
  const {
    scoreType = '音教成绩',
    minScore = 0,
    maxScore = 300,
    page = 1,
    pageSize = 20
  } = params

  // 映射成绩类型
  const apiScoreType = SCORE_TYPE_MAP[scoreType] || SCORE_TYPE_MAP['音教成绩']

  // 构建请求参数
  const requestParams = {
    score_type: apiScoreType,
    min_score: minScore,
    max_score: maxScore,
    page: page,
    page_size: pageSize
  }

  // 构建 URL
  const queryString = new URLSearchParams(requestParams).toString()
  const url = `${API_BASE_URL}/query_scores.php?${queryString}`

  console.log('请求URL:', url)
  
  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      console.log('响应状态:', response.status, response.statusText)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      console.log('API返回原始数据:', data)
      
      if (data.success && data.data) {
        // 检查数据是否存在
        if (!data.data.list || !Array.isArray(data.data.list)) {
          console.warn('数据格式异常，list不存在或不是数组:', data.data)
          return {
            success: true,
            data: {
              list: [],
              pagination: data.data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 },
              queryParams: data.data.query_params || {}
            }
          }
        }
        
        // 转换数据格式，映射字段名
        const transformedData = {
          list: data.data.list.map(item => transformStudentData(item, scoreType)),
          pagination: data.data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 },
          queryParams: data.data.query_params || {}
        }
        
        console.log('转换后的数据:', transformedData)
        
        return {
          success: true,
          data: transformedData
        }
      } else {
        console.error('API返回失败:', data.message, data.errors)
        return {
          success: false,
          message: data.message || '查询失败',
          errors: data.errors || []
        }
      }
    })
    .catch(error => {
      console.error('API请求失败:', error)
      return {
        success: false,
        message: '请求失败，请稍后重试',
        error: error.message
      }
    })
}

/**
 * 转换学生数据格式
 * @param {Object} item - 接口返回的学生数据
 * @param {string} scoreType - 成绩类型
 * @returns {Object} 转换后的数据
 */
function transformStudentData(item, scoreType) {
  console.log('转换数据项:', item, '成绩类型:', scoreType)
  
  // 根据成绩类型获取对应的分数
  let score = 0
  if (scoreType === '音教成绩') {
    score = parseFloat(item.music_education_score) || 0
  } else if (scoreType === '器表成绩') {
    score = parseFloat(item.performance_instrumental_score) || 0
  } else if (scoreType === '声表成绩') {
    score = parseFloat(item.performance_vocal_score) || 0
  }

  const maskedName = maskStudentName(item.name)
  
  // 获取视频URL和对应的视频路径（根据接口数据判断哪个有就展示哪个）
  const { videoUrl, videoPath } = getVideoUrlAndPath(item, scoreType)
  
  // 根据视频路径生成对应的缩略图URL
  let thumbnail = `https://via.placeholder.com/640x360/409eff/ffffff?text=${encodeURIComponent(maskedName)}`
  if (videoPath) {
    const campusCode = item.campus_code || ''
    thumbnail = buildThumbnailUrl(campusCode, videoPath)
  }
  
  const transformed = {
    id: parseInt(item.id) || 0,
    title: `${maskedName} - ${score.toFixed(2)}分`,
    description: item.introduction || '学生作品展示',
    fullDescription: item.introduction || '学生作品展示视频',
    thumbnail: thumbnail,
    videoUrl: videoUrl,
    duration: '00:00',
    views: 0,
    date: item.created_at ? item.created_at.split(' ')[0] : new Date().toISOString().split('T')[0],
    score: score,
    studentName: maskedName,
    campus: item.campus || '未知校区',
    campusCode: item.campus_code || '',
    pinyin: item.pinyin || '',
    class: item.class || 0,
    highSchool: item.high_school || '',
    remarks: item.remarks || '',
    musicEducationScore: parseFloat(item.music_education_score) || 0,
    instrumentPerformanceScore: parseFloat(item.performance_instrumental_score) || 0,
    vocalPerformanceScore: parseFloat(item.performance_vocal_score) || 0,
    // 视频相关字段（只有存在的视频才赋值）
    instrumentalVideo1: item.instrumental_video1 ? buildVideoUrl(item.campus_code || '', item.instrumental_video1) : '',
    instrumentalVideo2: item.instrumental_video2 ? buildVideoUrl(item.campus_code || '', item.instrumental_video2) : '',
    vocalVideo1: item.vocal_video1 ? buildVideoUrl(item.campus_code || '', item.vocal_video1) : '',
    vocalVideo2: item.vocal_video2 ? buildVideoUrl(item.campus_code || '', item.vocal_video2) : '',
    instrumentalVideo1Desc: item.instrumental_video1_desc || '',
    instrumentalVideo2Desc: item.instrumental_video2_desc || '',
    vocalVideo1Desc: item.vocal_video1_desc || '',
    vocalVideo2Desc: item.vocal_video2_desc || '',
    tags: ['作品展示', '学习成果'],
    // 原始数据
    rawData: item
  }
  
  console.log('转换后的数据项:', transformed)
  return transformed
}

/**
 * 构建视频URL
 * @param {string} campusCode - 校区代码
 * @param {string} videoPath - 视频路径
 * @returns {string} 完整的视频URL
 */
function buildVideoUrl(campusCode, videoPath) {
  if (!campusCode || !videoPath) {
    return ''
  }
  // 格式：cjdb.xyyxpx.com/{campus_code}/{video_path}.mp4
  return `http://cjdb.xyyxpx.com/${campusCode}/${videoPath}.mp4`
}

/**
 * 构建缩略图URL
 * @param {string} campusCode - 校区代码
 * @param {string} videoPath - 视频路径
 * @returns {string} 完整的缩略图URL
 */
function buildThumbnailUrl(campusCode, videoPath) {
  if (!campusCode || !videoPath) {
    return ''
  }
  // 格式：cjdb.xyyxpx.com/{campus_code}/{video_path}.jpg
  return `http://cjdb.xyyxpx.com/${campusCode}/${videoPath}.jpg`
}

/**
 * 获取视频URL和对应的视频路径
 * @param {Object} item - 学生数据
 * @param {string} scoreType - 成绩类型
 * @returns {Object} 包含 videoUrl 和 videoPath 的对象
 */
function getVideoUrlAndPath(item, scoreType) {
  const campusCode = item.campus_code || ''
  
  // 根据成绩类型选择对应的视频，优先使用对应类型的视频
  if (scoreType === '器表成绩') {
    // 优先使用器乐视频
    if (item.instrumental_video1) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.instrumental_video1),
        videoPath: item.instrumental_video1
      }
    } else if (item.instrumental_video2) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.instrumental_video2),
        videoPath: item.instrumental_video2
      }
    }
    // 如果器乐视频不存在，尝试其他视频
    if (item.vocal_video1) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.vocal_video1),
        videoPath: item.vocal_video1
      }
    } else if (item.vocal_video2) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.vocal_video2),
        videoPath: item.vocal_video2
      }
    }
  } else if (scoreType === '声表成绩') {
    // 优先使用声乐视频
    if (item.vocal_video1) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.vocal_video1),
        videoPath: item.vocal_video1
      }
    } else if (item.vocal_video2) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.vocal_video2),
        videoPath: item.vocal_video2
      }
    }
    // 如果声乐视频不存在，尝试其他视频
    if (item.instrumental_video1) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.instrumental_video1),
        videoPath: item.instrumental_video1
      }
    } else if (item.instrumental_video2) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.instrumental_video2),
        videoPath: item.instrumental_video2
      }
    }
  } else {
    // 音教成绩：优先使用器乐视频，其次声乐视频
    if (item.instrumental_video1) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.instrumental_video1),
        videoPath: item.instrumental_video1
      }
    } else if (item.vocal_video1) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.vocal_video1),
        videoPath: item.vocal_video1
      }
    } else if (item.instrumental_video2) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.instrumental_video2),
        videoPath: item.instrumental_video2
      }
    } else if (item.vocal_video2) {
      return {
        videoUrl: buildVideoUrl(campusCode, item.vocal_video2),
        videoPath: item.vocal_video2
      }
    }
  }
  
  // 如果没有视频，返回空对象
  return {
    videoUrl: '',
    videoPath: ''
  }
}

/**
 * 获取视频URL（保持向后兼容）
 * @param {Object} item - 学生数据
 * @param {string} scoreType - 成绩类型
 * @returns {string} 视频URL
 */
function getVideoUrl(item, scoreType) {
  const result = getVideoUrlAndPath(item, scoreType)
  return result.videoUrl
}

/**
 * 获取学生详细信息
 * @param {number} id - 学生ID
 * @returns {Promise} 返回学生详细信息
 */
export function getStudentDetail(id) {
  const url = `${API_BASE_URL}/get_student_detail.php?id=${id}`

  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            message: '未找到该学生信息',
            data: null
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        // 转换数据格式
        const transformedData = transformStudentDetailData(data.data)
        return {
          success: true,
          data: transformedData
        }
      } else {
        return {
          success: false,
          message: data.message || '查询失败',
          errors: data.errors || [],
          data: null
        }
      }
    })
    .catch(error => {
      console.error('API请求失败:', error)
      return {
        success: false,
        message: '请求失败，请稍后重试',
        error: error.message,
        data: null
      }
    })
}

/**
 * 转换学生详情数据格式
 * @param {Object} item - 接口返回的学生详情数据
 * @returns {Object} 转换后的数据
 */
function transformStudentDetailData(item) {
  const campusCode = item.campus_code || ''
  const maskedName = maskStudentName(item.name)
  
  // 获取主要视频（优先使用器乐视频1，如果没有则使用声乐视频1）
  let mainVideoUrl = ''
  let mainVideoThumbnail = ''
  
  if (item.instrumental_video1) {
    mainVideoUrl = buildVideoUrl(campusCode, item.instrumental_video1)
    mainVideoThumbnail = buildThumbnailUrl(campusCode, item.instrumental_video1)
  } else if (item.vocal_video1) {
    mainVideoUrl = buildVideoUrl(campusCode, item.vocal_video1)
    mainVideoThumbnail = buildThumbnailUrl(campusCode, item.vocal_video1)
  } else if (item.instrumental_video2) {
    mainVideoUrl = buildVideoUrl(campusCode, item.instrumental_video2)
    mainVideoThumbnail = buildThumbnailUrl(campusCode, item.instrumental_video2)
  } else if (item.vocal_video2) {
    mainVideoUrl = buildVideoUrl(campusCode, item.vocal_video2)
    mainVideoThumbnail = buildThumbnailUrl(campusCode, item.vocal_video2)
  }
  
  // 如果没有视频，使用占位图
  if (!mainVideoThumbnail) {
    mainVideoThumbnail = `https://via.placeholder.com/640x360/409eff/ffffff?text=${encodeURIComponent(maskedName)}`
  }

  return {
    id: parseInt(item.id) || 0,
    title: `${maskedName} - 学生作品展示`,
    description: item.introduction || '学生作品展示',
    fullDescription: item.introduction || '学生作品展示视频',
    thumbnail: mainVideoThumbnail,
    videoUrl: mainVideoUrl || '',
    duration: '00:00',
    views: 0,
    date: item.created_at ? item.created_at.split(' ')[0] : new Date().toISOString().split('T')[0],
    score: parseFloat(item.music_education_score) || 0, // 默认使用音教成绩
    studentName: maskedName,
    campus: item.campus || '未知校区',
    campusCode: item.campus_code || '',
    pinyin: item.pinyin || '',
    class: item.class || 0,
    highSchool: item.high_school || '',
    remarks: item.remarks || '',
    musicEducationScore: parseFloat(item.music_education_score) || 0,
    instrumentPerformanceScore: parseFloat(item.performance_instrumental_score) || 0,
    vocalPerformanceScore: parseFloat(item.performance_vocal_score) || 0,
    // 视频相关字段（只有存在的视频才赋值）
    instrumentalVideo1: item.instrumental_video1 ? buildVideoUrl(campusCode, item.instrumental_video1) : '',
    instrumentalVideo2: item.instrumental_video2 ? buildVideoUrl(campusCode, item.instrumental_video2) : '',
    vocalVideo1: item.vocal_video1 ? buildVideoUrl(campusCode, item.vocal_video1) : '',
    vocalVideo2: item.vocal_video2 ? buildVideoUrl(campusCode, item.vocal_video2) : '',
    instrumentalVideo1Desc: item.instrumental_video1_desc || '',
    instrumentalVideo2Desc: item.instrumental_video2_desc || '',
    vocalVideo1Desc: item.vocal_video1_desc || '',
    vocalVideo2Desc: item.vocal_video2_desc || '',
    tags: ['作品展示', '学习成果'],
    // 原始数据
    rawData: item
  }
}

/**
 * 通过校区和班级查询学生列表
 * @param {Object} params - 查询参数
 * @param {string} params.campus_code - 校区代码（sd-山大、ss-山师、sy-山艺）
 * @param {number} params.class - 班级编号（可选，不填则查询该校区全部班级）
 * @param {number} params.page - 页码
 * @param {number} params.page_size - 每页数量
 * @returns {Promise} 返回查询结果
 */
export function queryByCampusClass(params) {
  const {
    campus_code,
    class: classNum,
    page = 1,
    page_size = 20
  } = params

  // 构建请求参数
  const requestParams = {
    campus_code: campus_code,
    page: page,
    page_size: page_size
  }

  // 如果指定了班级，则添加班级参数
  if (classNum !== undefined && classNum !== null && classNum !== '') {
    requestParams.class = classNum
  }

  // 构建 URL
  const queryString = new URLSearchParams(requestParams).toString()
  const url = `${API_BASE_URL}/query_by_campus_class.php?${queryString}`

  console.log('请求URL:', url)
  
  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(response => {
      console.log('响应状态:', response.status, response.statusText)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      console.log('API返回原始数据:', data)
      
      if (data.success && data.data) {
        // 检查数据是否存在
        if (!data.data.list || !Array.isArray(data.data.list)) {
          console.warn('数据格式异常，list不存在或不是数组:', data.data)
          return {
            success: true,
            data: {
              list: [],
              pagination: data.data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 },
              query_params: data.data.query_params || {}
            }
          }
        }
        
        // 转换数据格式
        const transformedData = {
          list: data.data.list.map(item => transformCampusClassData(item)),
          pagination: data.data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 },
          query_params: data.data.query_params || {}
        }
        
        console.log('转换后的数据:', transformedData)
        
        return {
          success: true,
          data: transformedData
        }
      } else {
        console.error('API返回失败:', data.message, data.errors)
        return {
          success: false,
          message: data.message || '查询失败',
          errors: data.errors || []
        }
      }
    })
    .catch(error => {
      console.error('API请求失败:', error)
      return {
        success: false,
        message: '请求失败，请稍后重试',
        error: error.message
      }
    })
}

/**
 * 转换校区班级查询的学生数据格式
 * @param {Object} item - 接口返回的学生数据
 * @returns {Object} 转换后的数据
 */
function transformCampusClassData(item) {
  const campusCode = item.campus_code || ''
  const maskedName = maskStudentName(item.name)
  
  // 获取视频URL和对应的视频路径（优先使用器乐视频1）
  let videoUrl = ''
  let videoPath = ''
  let thumbnail = ''
  
  if (item.instrumental_video1) {
    videoPath = item.instrumental_video1
    videoUrl = buildVideoUrl(campusCode, videoPath)
    thumbnail = buildThumbnailUrl(campusCode, videoPath)
  } else if (item.vocal_video1) {
    videoPath = item.vocal_video1
    videoUrl = buildVideoUrl(campusCode, videoPath)
    thumbnail = buildThumbnailUrl(campusCode, videoPath)
  } else if (item.instrumental_video2) {
    videoPath = item.instrumental_video2
    videoUrl = buildVideoUrl(campusCode, videoPath)
    thumbnail = buildThumbnailUrl(campusCode, videoPath)
  } else if (item.vocal_video2) {
    videoPath = item.vocal_video2
    videoUrl = buildVideoUrl(campusCode, videoPath)
    thumbnail = buildThumbnailUrl(campusCode, videoPath)
  }
  
  // 如果没有视频，使用占位图
  if (!thumbnail) {
    thumbnail = `https://via.placeholder.com/640x360/409eff/ffffff?text=${encodeURIComponent(maskedName)}`
  }
  
  return {
    id: parseInt(item.id) || 0,
    title: `${maskedName} - 学生作品展示`,
    description: item.introduction || '学生作品展示',
    fullDescription: item.introduction || '学生作品展示视频',
    thumbnail: thumbnail,
    videoUrl: videoUrl,
    duration: '00:00',
    views: 0,
    date: item.created_at ? item.created_at.split(' ')[0] : new Date().toISOString().split('T')[0],
    studentName: maskedName,
    campus: item.campus || '未知校区',
    campusCode: item.campus_code || '',
    pinyin: item.pinyin || '',
    class: item.class || 0,
    highSchool: item.high_school || '',
    remarks: item.remarks || '',
    musicEducationScore: parseFloat(item.music_education_score) || 0,
    instrumentPerformanceScore: parseFloat(item.performance_instrumental_score) || 0,
    vocalPerformanceScore: parseFloat(item.performance_vocal_score) || 0,
    // 视频相关字段（只有存在的视频才赋值）
    instrumentalVideo1: item.instrumental_video1 ? buildVideoUrl(campusCode, item.instrumental_video1) : '',
    instrumentalVideo2: item.instrumental_video2 ? buildVideoUrl(campusCode, item.instrumental_video2) : '',
    vocalVideo1: item.vocal_video1 ? buildVideoUrl(campusCode, item.vocal_video1) : '',
    vocalVideo2: item.vocal_video2 ? buildVideoUrl(campusCode, item.vocal_video2) : '',
    instrumentalVideo1Desc: item.instrumental_video1_desc || '',
    instrumentalVideo2Desc: item.instrumental_video2_desc || '',
    vocalVideo1Desc: item.vocal_video1_desc || '',
    vocalVideo2Desc: item.vocal_video2_desc || '',
    tags: ['作品展示', '学习成果'],
    // 原始数据
    rawData: item
  }
}

export { SCORE_TYPE_MAP, SCORE_TYPE_REVERSE_MAP }

